import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Plus, User } from "lucide-react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import TextInput from "presentation/components/UI/TextInput";
import { Button } from "presentation/components/UI/Button";
import { orderRecognitionRuleUseCase } from "../../../../di/container";
import type {
  CustomerEmailItem,
  CustomerSuggestion,
} from "../../../../domain/types/order-recognition-rule";
import RuleBlockCard, { type RuleBlockVariant } from "./RuleBlockCard";
import NewCustomerDialog, { type NewCustomerData } from "./NewCustomerDialog";

interface CustomerEmailForm {
  email: string;
}

interface CustomerEmailsBlockProps {
  onNotify: (message: string) => void;
  variant?: RuleBlockVariant;
}

/** 등록된 고객 메일 블럭: 보낸 사람이 목록에 있으면 고객 정보를 주문으로 가져옴 */
const CustomerEmailsBlock: React.FC<CustomerEmailsBlockProps> = ({
  onNotify,
  variant = "card",
}) => {
  const { companyId } = useParams();
  const [customerEmails, setCustomerEmails] = useState<CustomerEmailItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // 새 고객 등록 다이얼로그 상태
  const [isNewCustomerDialogOpen, setIsNewCustomerDialogOpen] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  // 자동완성에서 선택한 이메일인지 구분하기 위한 플래그
  const isFromSuggestionRef = useRef(false);

  const customerEmailForm = useForm<CustomerEmailForm>({
    defaultValues: { email: "" },
  });

  // 이메일 자동완성 추천
  const [suggestions, setSuggestions] = useState<CustomerSuggestion[]>([]);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  // 모달의 overflow에 잘리지 않도록 드롭다운은 body로 포털 렌더링하고 위치를 직접 계산한다
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const suggestBoxRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // 추천 항목 선택 직후의 입력값 변경에 대해서는 다시 추천을 띄우지 않기 위한 플래그
  const skipNextSuggestRef = useRef(false);

  const emailValue = customerEmailForm.watch("email");

  const updateDropdownPosition = useCallback(() => {
    if (!suggestBoxRef.current) return;
    const rect = suggestBoxRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  // 입력값이 바뀌면 디바운스 후 추천 목록을 가져온다
  useEffect(() => {
    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    if (skipNextSuggestRef.current) {
      skipNextSuggestRef.current = false;
      return;
    }

    const keyword = emailValue.trim();
    if (keyword.length < 1) {
      setSuggestions([]);
      setIsSuggestionsOpen(false);
      return;
    }

    let isActive = true;
    const timer = setTimeout(async () => {
      try {
        const result = await orderRecognitionRuleUseCase.suggestCustomerEmails(
          cid,
          keyword,
        );
        if (!isActive) return;
        setSuggestions(result);
        if (result.length > 0) {
          updateDropdownPosition();
          setIsSuggestionsOpen(true);
        } else {
          setIsSuggestionsOpen(false);
        }
      } catch (error) {
        console.error("Error fetching customer email suggestions:", error);
        if (!isActive) return;
        setSuggestions([]);
        setIsSuggestionsOpen(false);
      }
    }, 250);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [emailValue, companyId]);

  // 추천 목록이 열려 있는 동안 스크롤/리사이즈에 맞춰 위치를 갱신한다
  useEffect(() => {
    if (!isSuggestionsOpen) return;

    updateDropdownPosition();
    // 모달 내부 스크롤까지 잡기 위해 capture 단계에서 수신한다
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);

    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [isSuggestionsOpen, updateDropdownPosition]);

  // 바깥 영역(입력란/드롭다운 모두) 클릭 시 추천 목록을 닫는다
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideTrigger = suggestBoxRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideTrigger && !insideDropdown) {
        setIsSuggestionsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectSuggestion = (suggestion: CustomerSuggestion) => {
    if (!suggestion.email) return;
    skipNextSuggestRef.current = true;
    isFromSuggestionRef.current = true; // 자동완성에서 선택했음을 표시
    customerEmailForm.setValue("email", suggestion.email, {
      shouldValidate: true,
    });
    setIsSuggestionsOpen(false);
  };

  // 등록된 고객 메일 목록을 불러온다
  useEffect(() => {
    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    const fetchCustomerEmails = async () => {
      try {
        const emails = await orderRecognitionRuleUseCase.getCustomerEmails(cid);
        setCustomerEmails(emails);
      } catch (error) {
        console.error("Error fetching customer emails:", error);
      }
    };

    fetchCustomerEmails();
  }, [companyId]);

  const handleAddCustomerEmail = async (data: CustomerEmailForm) => {
    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    const email = data.email.trim();
    if (!email) return;

    if (
      customerEmails.some((c) => c.email.toLowerCase() === email.toLowerCase())
    ) {
      onNotify("이미 등록된 고객 메일입니다.");
      return;
    }

    // 자동완성에서 선택한 경우 → 기존 유저이므로 바로 추가
    if (isFromSuggestionRef.current) {
      isFromSuggestionRef.current = false;
      await addCustomerEmailDirectly(email);
      return;
    }

    // 직접 입력한 경우 → 새 고객 다이얼로그 표시
    setPendingEmail(email);
    setIsNewCustomerDialogOpen(true);
  };

  /** 이름/전화번호 없이 바로 고객 메일 추가 (기존 유저 연결) */
  const addCustomerEmailDirectly = async (email: string) => {
    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    setIsSaving(true);
    try {
      const saved = await orderRecognitionRuleUseCase.addCustomerEmail(cid, {
        email,
      });
      setCustomerEmails((prev) => [...prev, saved]);
      customerEmailForm.reset({ email: "" });
      onNotify("고객 메일이 추가되었습니다.");
    } catch (error) {
      console.error("Error adding customer email:", error);
      onNotify("고객 메일 추가에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  /** 새 고객 다이얼로그에서 제출 시 호출 */
  const handleNewCustomerSubmit = async (data: NewCustomerData) => {
    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    setIsSaving(true);
    try {
      const saved = await orderRecognitionRuleUseCase.addCustomerEmail(cid, {
        email: pendingEmail,
        name: data.name,
        mobile: data.mobile,
      });
      setCustomerEmails((prev) => [...prev, saved]);
      customerEmailForm.reset({ email: "" });
      setIsNewCustomerDialogOpen(false);
      setPendingEmail("");
      onNotify("새 고객이 등록되었습니다.");
    } catch (error) {
      console.error("Error adding new customer:", error);
      onNotify("고객 등록에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleNewCustomerDialogClose = () => {
    setIsNewCustomerDialogOpen(false);
    setPendingEmail("");
  };

  const handleDeleteCustomerEmail = async (id: number) => {
    const cid = Number(companyId);
    if (!Number.isFinite(cid)) return;

    setIsSaving(true);
    try {
      await orderRecognitionRuleUseCase.deleteCustomerEmail(cid, id);
      setCustomerEmails((prev) => prev.filter((c) => c.id !== id));
      onNotify("고객 메일이 삭제되었습니다.");
    } catch (error) {
      console.error("Error deleting customer email:", error);
      onNotify("고객 메일 삭제에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <RuleBlockCard
      variant={variant}
      icon={<User className="text-[#4285F4]" />}
      iconBgClassName="bg-[#E7EFFF]"
      title="등록된 고객 메일"
      description="보낸 사람이 아래 목록에 존재한다면 고객 정보를 주문으로 가져옵니다."
    >
      <form
        className="flex flex-row items-start gap-2 w-full"
        onSubmit={customerEmailForm.handleSubmit(handleAddCustomerEmail)}
      >
        <div ref={suggestBoxRef} className="relative w-full">
          <TextInput
            className="w-full"
            placeholder="고객 메일을 입력하세요"
            autoComplete="off"
            onFocus={() => {
              if (suggestions.length > 0) {
                updateDropdownPosition();
                setIsSuggestionsOpen(true);
              }
            }}
            error={
              customerEmailForm.formState.errors.email
                ? {
                    message:
                      customerEmailForm.formState.errors.email.message ?? "",
                    isMessageVisible: true,
                    isIconVisible: true,
                  }
                : undefined
            }
            {...customerEmailForm.register("email", {
              required: "필수 항목이에요.",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "올바른 이메일 형식이 아니에요.",
              },
            })}
          />

          {isSuggestionsOpen && suggestions.length > 0
            ? ReactDOM.createPortal(
                <div
                  ref={dropdownRef}
                  className="fixed z-[100002]"
                  style={{
                    top: dropdownPos.top,
                    left: dropdownPos.left,
                    width: dropdownPos.width,
                  }}
                >
                  <ul className="max-h-[240px] overflow-y-auto bg-white border border-[#d9d9d9] rounded-[8px] shadow-lg">
                    {suggestions.map((suggestion) => (
                      <li key={suggestion.userId}>
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-[#E7EFFF] flex flex-col"
                          onClick={() => handleSelectSuggestion(suggestion)}
                        >
                          <span className="text-[14px] truncate">
                            {suggestion.email ?? "-"}
                          </span>
                          {suggestion.name || suggestion.mobile ? (
                            <span className="text-[12px] text-gray-500 truncate">
                              {[suggestion.name, suggestion.mobile]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>,
                document.body,
              )
            : null}
        </div>
        <Button
          className="w-full max-w-[120px] h-[46px]"
          variant="primary"
          type="submit"
          disabled={isSaving}
        >
          고객 메일 추가
        </Button>
      </form>

      {customerEmails.length === 0 ? (
        <div className="mt-4 text-[13px] text-gray-400">
          등록된 고객 메일이 없습니다.
        </div>
      ) : (
        <div className="text-start grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
          {customerEmails.map((customer) => (
            <div
              key={customer.id}
              className="flex flex-row justify-between items-center bg-gray-100 border border-[#e0e0e0] rounded-[13px] py-2 px-2"
            >
              <div className="ml-1 text-[14px] truncate">{customer.email}</div>

              <Button
                className="w-[25px] h-[25px] flex-shrink-0 p-0"
                variant="danger"
                size="sm"
                disabled={isSaving}
                onClick={() => handleDeleteCustomerEmail(customer.id)}
              >
                <Plus className="rotate-45 text-white" size={16} />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* 새 고객 등록 다이얼로그 */}
      <NewCustomerDialog
        isOpen={isNewCustomerDialogOpen}
        email={pendingEmail}
        onClose={handleNewCustomerDialogClose}
        onSubmit={handleNewCustomerSubmit}
        isLoading={isSaving}
      />
    </RuleBlockCard>
  );
};

export default CustomerEmailsBlock;
